/**
 * Haircut Formulas and Recognition Rates for MAS TDSR/MSR Calculations
 * 
 * Source: MAS Notice 632 - Property Loans
 * https://www.mas.gov.sg/regulation/banking/notices-and-circulars/notice-632-property-loans
 */

export interface HaircutInfo {
  field: string;
  haircutPercent: number;
  recognitionPercent: number;
  formula: string;
  description: string;
  source: {
    name: string;
    url: string;
    section?: string;
  };
}

/**
 * Haircut information for each profile field
 */
export const HAIRCUT_INFO: Record<string, HaircutInfo> = {
  monthlyIncome: {
    field: 'Monthly Income (Base Salary)',
    haircutPercent: 0,
    recognitionPercent: 100,
    formula: 'Assessable Monthly Income = Monthly Income × 100%',
    description: 'Base monthly salary is recognized at 100% (no haircut) as it represents stable, guaranteed income.',
    source: {
      name: 'MAS Notice 632',
      url: 'https://www.mas.gov.sg/regulation/banking/notices-and-circulars/notice-632-property-loans',
      section: 'Income Recognition - Fixed Income',
    },
  },
  otherAnnualIncome: {
    field: 'Other Annual Income (Variable Income)',
    haircutPercent: 30,
    recognitionPercent: 70,
    formula: 'Assessable Monthly Income = (Other Annual Income ÷ 12) × 70%',
    description: 'Variable income (commission, bonuses, incentives, overtime) is recognized at 70% (30% haircut) to account for income volatility and uncertainty.',
    source: {
      name: 'MAS Notice 632',
      url: 'https://www.mas.gov.sg/regulation/banking/notices-and-circulars/notice-632-property-loans',
      section: 'Income Recognition - Variable Income',
    },
  },
  assessableIncome: {
    field: 'Assessable Income (Monthly)',
    haircutPercent: 0,
    recognitionPercent: 100,
    formula: 'Assessable Monthly Income = Monthly Income + [(Other Annual Income ÷ 12) × 70%]',
    description: 'Total assessable monthly income combines base salary (100%) and variable income (70% recognition). This is used for TDSR and MSR calculations.',
    source: {
      name: 'MAS Notice 632',
      url: 'https://www.mas.gov.sg/regulation/banking/notices-and-circulars/notice-632-property-loans',
      section: 'Total Debt Servicing Ratio (TDSR)',
    },
  },
  age: {
    field: 'Age',
    haircutPercent: 0,
    recognitionPercent: 100,
    formula: 'Age determines the MAS Loan Assessment Rate used for TDSR calculations',
    description: 'Age is used to determine the appropriate MAS Loan Assessment Rate. Younger borrowers typically use higher assessment rates (e.g., 3.5% or 4.0%) to stress-test loan affordability.',
    source: {
      name: 'MAS Notice 632',
      url: 'https://www.mas.gov.sg/regulation/banking/notices-and-circulars/notice-632-property-loans',
      section: 'Loan Assessment Rate',
    },
  },
  carLoan: {
    field: 'Car Loan',
    haircutPercent: 0,
    recognitionPercent: 100,
    formula: 'Monthly Debt = Car Loan × 100%',
    description: 'Car loan monthly payments are recognized at 100% (no haircut) as they represent fixed monthly obligations.',
    source: {
      name: 'MAS Notice 632',
      url: 'https://www.mas.gov.sg/regulation/banking/notices-and-circulars/notice-632-property-loans',
      section: 'Total Debt Servicing Ratio (TDSR)',
    },
  },
  housingLoan: {
    field: 'Housing Loan',
    haircutPercent: 0,
    recognitionPercent: 100,
    formula: 'Monthly Debt = Housing Loan × 100%',
    description: 'Existing housing loan monthly payments are recognized at 100% (no haircut) as they represent fixed monthly obligations.',
    source: {
      name: 'MAS Notice 632',
      url: 'https://www.mas.gov.sg/regulation/banking/notices-and-circulars/notice-632-property-loans',
      section: 'Total Debt Servicing Ratio (TDSR)',
    },
  },
  creditCard: {
    field: 'Credit Card',
    haircutPercent: 0,
    recognitionPercent: 100,
    formula: 'Monthly Debt = Credit Card Minimum Payment × 100%',
    description: 'Credit card minimum monthly payments are recognized at 100% (no haircut) as they represent fixed monthly obligations.',
    source: {
      name: 'MAS Notice 632',
      url: 'https://www.mas.gov.sg/regulation/banking/notices-and-circulars/notice-632-property-loans',
      section: 'Total Debt Servicing Ratio (TDSR)',
    },
  },
  otherLoan: {
    field: 'Other Loan',
    haircutPercent: 0,
    recognitionPercent: 100,
    formula: 'Monthly Debt = Other Loan × 100%',
    description: 'Other loan monthly payments are recognized at 100% (no haircut) as they represent fixed monthly obligations.',
    source: {
      name: 'MAS Notice 632',
      url: 'https://www.mas.gov.sg/regulation/banking/notices-and-circulars/notice-632-property-loans',
      section: 'Total Debt Servicing Ratio (TDSR)',
    },
  },
};

/**
 * Calculate assessable monthly income from profile fields.
 * When selfEmployedInd is true/1, monthly income is recognised at 70% (30% haircut)
 * per MAS Notice 632 treatment of self-employed income.
 */
export const calculateAssessableIncome = (
  monthlyIncome: string,
  otherAnnualIncome: string,
  selfEmployedInd?: number | boolean | null
): number => {
  const monthly = parseFloat(monthlyIncome || '0');
  const annual = parseFloat(otherAnnualIncome || '0');
  
  // Self-employed: 70% recognition on base monthly income (30% haircut)
  const isSelfEmployed = selfEmployedInd === 1 || selfEmployedInd === true;
  const assessableBase = isSelfEmployed ? monthly * 0.7 : monthly;
  
  // Variable income: 70% recognition (30% haircut)
  const assessableVariable = (annual / 12) * 0.7;
  
  return assessableBase + assessableVariable;
};

/**
 * Get haircut information for a field
 */
export const getHaircutInfo = (field: string): HaircutInfo | null => {
  return HAIRCUT_INFO[field] || null;
};
